import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import Button from "../../components/ui/button/Button";
import { getC103Form, C103Form } from "../../lib/api/c103";
import { apiPost } from "../../lib/api-client";
import { useToast } from "../../contexts/ToastContext";
import DWRUploader, { DWRPreviewData } from "../../components/nm/DWRUploader";
import OperationsTimeline, { TimelineDay } from "../../components/nm/OperationsTimeline";
import PlugReconciliation, {
  PlugComparisonRow,
  ReconciliationSummary,
} from "../../components/nm/PlugReconciliation";
import AbbreviationTooltip from "../../components/ui/AbbreviationTooltip";

type SubsequentStep = "upload" | "review" | "reconciliation" | "export";

const STEPS: { key: SubsequentStep; label: string; number: number }[] = [
  { key: "upload", label: "Upload DWRs", number: 1 },
  { key: "review", label: "Operations Review", number: 2 },
  { key: "reconciliation", label: "Plug Reconciliation", number: 3 },
  { key: "export", label: "Report & Export", number: 4 },
];

interface SubsequentReportPayload {
  form_type: string;
  report_narrative?: string;
  dwr_data?: DWRPreviewData;
}

interface SubsequentReportResponse {
  id: number;
  status: string;
  pdf_url?: string;
  report_narrative?: string;
  compliance_checks?: Array<{ rule: string; status: string; description: string }>;
}

interface ReconciliationResult {
  comparisons: PlugComparisonRow[];
  summary: ReconciliationSummary;
  narrative?: string;
}

function StepIndicator({ currentStep }: { currentStep: SubsequentStep }) {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);

  return (
    <nav className="flex items-center gap-0 mb-6">
      {STEPS.map((step, idx) => {
        const isDone = idx < currentIndex;
        const isActive = idx === currentIndex;

        return (
          <div key={step.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${
                  isDone
                    ? "bg-green-500 text-white"
                    : isActive
                    ? "bg-brand-500 text-white"
                    : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                }`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.number
                )}
              </div>
              <span
                className={`hidden sm:block text-sm font-medium whitespace-nowrap ${
                  isActive
                    ? "text-brand-600 dark:text-brand-400"
                    : isDone
                    ? "text-green-600 dark:text-green-400"
                    : "text-gray-400 dark:text-gray-500"
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 transition-colors ${
                  idx < currentIndex
                    ? "bg-green-400 dark:bg-green-600"
                    : "bg-gray-200 dark:bg-gray-700"
                }`}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}

function NOISummaryCard({ form }: { form: C103Form }) {
  const planData = form.plan_data as Record<string, unknown>;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
          NOI Summary — C-103 Form #{form.id}
        </h3>
      </div>
      <div className="p-6 grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm">
        {[
          { label: "API Number", value: form.api_number },
          { label: "Region", value: form.region || "—" },
          { label: "Status", value: form.status.replace(/_/g, " ") },
          {
            label: "Planned Plugs",
            value: String((form.plugs ?? []).length),
          },
          {
            label: "Operator",
            value: String(planData?.operator_name ?? "—"),
          },
          {
            label: "Lease",
            value: String(planData?.lease_name ?? "—"),
          },
          { label: "COA Figure", value: form.coa_figure || "—" },
          {
            label: "Submitted",
            value: form.submitted_at
              ? new Date(form.submitted_at).toLocaleDateString()
              : "—",
          },
        ].map((item) => (
          <div key={item.label}>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              {item.label}
            </p>
            <p className="mt-0.5 font-semibold text-gray-800 dark:text-white/90 capitalize">
              {item.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SubsequentReport() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [form, setForm] = useState<C103Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [currentStep, setCurrentStep] = useState<SubsequentStep>("upload");

  // Step 1 state
  const [dwrData, setDwrData] = useState<DWRPreviewData | null>(null);

  // Step 2 state
  const [timelineDays, setTimelineDays] = useState<TimelineDay[]>([]);

  // Step 3 state
  const [reconciliation, setReconciliation] = useState<ReconciliationResult | null>(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);

  // Step 4 state
  const [reportResult, setReportResult] = useState<SubsequentReportResponse | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportNarrative, setReportNarrative] = useState<string>("");
  const [complianceChecks, setComplianceChecks] = useState<
    Array<{ rule: string; status: string; description: string }>
  >([]);

  useEffect(() => {
    const loadForm = async () => {
      if (!formId) {
        setPageError("Form ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getC103Form(Number(formId));
        setForm(data);
      } catch (err) {
        setPageError(
          err instanceof Error ? err.message : "Failed to load C-103 form."
        );
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [formId]);

  const handleUploadComplete = (data: DWRPreviewData) => {
    setDwrData(data);
    // Populate timeline from DWR data
    const days: TimelineDay[] = data.daily_summaries.map((ds) => ({
      day_number: ds.day_number,
      work_date: ds.work_date,
      narrative: ds.narrative,
      events: ds.events.map((ev) => ({
        event_type: ev.event_type,
        description: ev.description,
        depth_top_ft: ev.depth_top_ft,
        depth_bottom_ft: ev.depth_bottom_ft,
      })),
    }));
    setTimelineDays(days);
  };

  const handleProceedToReview = () => {
    if (!dwrData) {
      showError("Upload Required", "Please upload DWR files before proceeding.");
      return;
    }
    setCurrentStep("review");
  };

  const handleProceedToReconciliation = async () => {
    if (!formId || !dwrData) return;
    setReconciliationLoading(true);
    setCurrentStep("reconciliation");

    try {
      const result = await apiPost<ReconciliationResult>(
        `/api/c103/forms/${formId}/reconcile/`,
        {
          actual_plugs: dwrData.actual_plugs,
          daily_summaries: timelineDays,
        }
      );
      setReconciliation(result);
      if (result.narrative) {
        setReportNarrative(result.narrative);
      }
    } catch (err) {
      showError(
        "Reconciliation Failed",
        err instanceof Error ? err.message : "Failed to run plug reconciliation."
      );
    } finally {
      setReconciliationLoading(false);
    }
  };

  const handleProceedToExport = () => {
    if (!reconciliation) {
      showError("Reconciliation Required", "Please complete plug reconciliation before proceeding.");
      return;
    }
    setCurrentStep("export");
  };

  const handleGenerateReport = async (saveAsDraft = false) => {
    if (!formId) return;
    setReportLoading(true);

    try {
      const payload: SubsequentReportPayload = {
        form_type: "subsequent",
        report_narrative: reportNarrative,
        dwr_data: dwrData ?? undefined,
      };

      if (saveAsDraft) {
        Object.assign(payload, { save_as_draft: true });
      }

      const result = await apiPost<SubsequentReportResponse>(
        `/api/c103/forms/${formId}/subsequent-report/`,
        payload
      );

      setReportResult(result);
      if (result.compliance_checks) {
        setComplianceChecks(result.compliance_checks);
      }

      success(
        saveAsDraft ? "Draft Saved" : "Report Generated",
        saveAsDraft
          ? "Subsequent report saved as draft."
          : "Subsequent report generated successfully."
      );
    } catch (err) {
      showError(
        "Generation Failed",
        err instanceof Error ? err.message : "Failed to generate subsequent report."
      );
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb pageTitle="Subsequent Report" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
            <p className="mt-3 text-gray-600 dark:text-gray-400">Loading form...</p>
          </div>
        </div>
      </>
    );
  }

  if (pageError || !form) {
    return (
      <>
        <PageBreadcrumb pageTitle="Subsequent Report" />
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-6">
          <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
            <p className="text-sm font-medium text-error-700 dark:text-error-400">
              {pageError || "Form not found"}
            </p>
            <Button onClick={() => navigate("/filings")} className="mt-4" variant="outline">
              Back to Filings
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageMeta
        title={`Subsequent Report — C-103 #${form.id} | RegulAgent`}
        description="Create subsequent report for plug and abandon operations"
      />

      <PageBreadcrumb pageTitle={`Subsequent Report — ${form.api_number}`} />

      <StepIndicator currentStep={currentStep} />

      {/* NOI Summary always visible */}
      <div className="mb-6">
        <NOISummaryCard form={form} />
      </div>

      {/* Step 1: Upload DWRs */}
      {currentStep === "upload" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Step 1: Upload Daily Work Reports (<AbbreviationTooltip term="DWR" />)
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Upload DWR PDFs to extract operations timeline and plug data.
              </p>
            </div>
            <div className="p-6">
              <DWRUploader
                formId={form.id}
                onUploadComplete={handleUploadComplete}
                onError={(msg) => showError("Upload Failed", msg)}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleProceedToReview} disabled={!dwrData}>
              Next: Operations Review
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Operations Review */}
      {currentStep === "review" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                    Step 2: Operations Review
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Review and edit the day-by-day operations timeline extracted from DWRs.
                  </p>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {timelineDays.length} day{timelineDays.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="p-6">
              <OperationsTimeline
                days={timelineDays}
                editable
                onDaysChange={setTimelineDays}
              />
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep("upload")}>
              Back
            </Button>
            <Button onClick={handleProceedToReconciliation}>
              Next: Plug Reconciliation
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Plug Reconciliation */}
      {currentStep === "reconciliation" && (
        <div className="space-y-6">
          {reconciliationLoading ? (
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
                <p className="mt-3 text-gray-600 dark:text-gray-400">
                  Running plug reconciliation...
                </p>
              </div>
            </div>
          ) : reconciliation ? (
            <PlugReconciliation
              comparisons={reconciliation.comparisons}
              summary={reconciliation.summary}
            />
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No reconciliation data available.
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep("review")}>
              Back
            </Button>
            <Button onClick={handleProceedToExport} disabled={!reconciliation}>
              Next: Report &amp; Export
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Report Review & Export */}
      {currentStep === "export" && (
        <div className="space-y-6">
          {/* Narrative preview / editor */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Step 4: Report Review &amp; Export
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Complete Narrative
                </label>
                <textarea
                  value={reportNarrative}
                  onChange={(e) => setReportNarrative(e.target.value)}
                  rows={10}
                  placeholder="Full operations narrative will appear here after reconciliation..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y font-mono"
                />
              </div>

              {/* DWR summary stats */}
              {dwrData && (
                <div className="rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] p-4">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                    Operations Summary
                  </p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
                    {[
                      { label: "Total Days", value: String(dwrData.total_days) },
                      { label: "Actual Plugs", value: String(dwrData.actual_plugs.length) },
                      { label: "Start Date", value: dwrData.start_date },
                      { label: "End Date", value: dwrData.end_date },
                    ].map((item) => (
                      <div key={item.label}>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {item.label}
                        </p>
                        <p className="mt-0.5 font-semibold text-gray-800 dark:text-white/90">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reconciliation summary */}
              {reconciliation && (
                <div className="rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] p-4">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                    Reconciliation Summary
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="text-green-700 dark:text-green-400 font-medium">
                      {reconciliation.summary.matches} matches
                    </span>
                    {reconciliation.summary.minor_deviations > 0 && (
                      <span className="text-amber-700 dark:text-amber-400 font-medium">
                        {reconciliation.summary.minor_deviations} minor deviations
                      </span>
                    )}
                    {reconciliation.summary.major_deviations > 0 && (
                      <span className="text-red-700 dark:text-red-400 font-medium">
                        {reconciliation.summary.major_deviations} major deviations
                      </span>
                    )}
                    {reconciliation.summary.added_plugs > 0 && (
                      <span className="text-blue-700 dark:text-blue-400 font-medium">
                        {reconciliation.summary.added_plugs} added
                      </span>
                    )}
                    {reconciliation.summary.missing_plugs > 0 && (
                      <span className="text-gray-600 dark:text-gray-400 font-medium">
                        {reconciliation.summary.missing_plugs} missing
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Overall: {reconciliation.summary.overall_status}
                  </p>
                </div>
              )}

              {/* Compliance checks (after report generated) */}
              {complianceChecks.length > 0 && (
                <div className="rounded-xl border border-gray-200 dark:border-white/[0.05]">
                  <p className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-white/[0.05]">
                    Compliance Checks
                  </p>
                  <div className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {complianceChecks.map((chk, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                        <span
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            chk.status === "pass"
                              ? "bg-green-500"
                              : chk.status === "warning"
                              ? "bg-amber-500"
                              : "bg-red-500"
                          }`}
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium flex-shrink-0">
                          {chk.rule}
                        </span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                          {chk.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Report generated success */}
              {reportResult && (
                <div className="rounded-xl border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-900/10 p-4">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                    Subsequent Report #{reportResult.id} created successfully
                  </p>
                  <div className="mt-2 flex gap-3">
                    {reportResult.pdf_url && (
                      <a
                        href={reportResult.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-brand-600 dark:text-brand-400 hover:underline font-medium"
                      >
                        Download PDF
                      </a>
                    )}
                    <button
                      onClick={() => navigate(`/c103/${reportResult.id}`)}
                      className="text-sm text-brand-600 dark:text-brand-400 hover:underline font-medium"
                    >
                      View Form
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between flex-wrap gap-3">
            <Button variant="outline" onClick={() => setCurrentStep("reconciliation")}>
              Back
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => handleGenerateReport(true)}
                disabled={reportLoading}
              >
                {reportLoading ? "Saving..." : "Save as Draft"}
              </Button>
              <Button
                onClick={() => handleGenerateReport(false)}
                disabled={reportLoading}
              >
                {reportLoading ? "Generating..." : "Generate Report"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
